import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faLink, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faXTwitter, faLinkedinIn } from '@fortawesome/free-brands-svg-icons';
import './ArticleLayout.css';

const ArticleLayout = ({ title, subtitle, author, readTime, children }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const currentUrl = window.location.href;
  const shareText = `${title} — ${subtitle}`;

  // --- Share handlers ---
  // UTM params appended to shared URLs for referral tracking:
  //   utm_source  = where the link was shared (linkedin, twitter, copy_link)
  //   utm_medium  = channel type (social, share)
  //   utm_campaign = foundations (groups all article shares)
  // When someone clicks a shared link and lands on curvsort.com,
  // these params appear in analytics so we know which channel drove traffic.

  const handleCopyLink = () => {
    const shareUrl = `${currentUrl}?utm_source=copy_link&utm_medium=share&utm_campaign=foundations`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTwitterShare = () => {
    const shareUrl = `${currentUrl}?utm_source=twitter&utm_medium=social&utm_campaign=foundations`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // LinkedIn: uses shareActive=true&text= to pre-fill the compose box
  // with article title + URL (editable by user before posting).
  // No callback/webhook exists — we cannot confirm if user actually posted.
  const handleLinkedInShare = () => {
    const shareUrl = `${currentUrl}?utm_source=linkedin&utm_medium=social&utm_campaign=foundations`;
    const text = `${title}\n\n${shareUrl}`;
    const url = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="article-layout">
      <div className="article-layout__nav">
        <button
          className="article-layout__back"
          onClick={() => navigate('/foundations')}
          type="button"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Foundations</span>
        </button>
      </div>

      <article className="article-layout__content">
        <header className="article-layout__header">
          <h1 className="article-layout__title">{title}</h1>
          {subtitle && <p className="article-layout__subtitle">{subtitle}</p>}
          <div className="article-layout__meta">
            <span className="article-layout__author">{author || 'Rajeev Bakshi'}</span>
            <span className="article-layout__separator">·</span>
            <span className="article-layout__org">CurvSort</span>
            {readTime && (
              <>
                <span className="article-layout__separator">·</span>
                <span className="article-layout__readtime">{readTime}</span>
              </>
            )}
          </div>
        </header>

        <div className="article-layout__body">
          {children}
        </div>

        <footer className="article-layout__footer">
          <div className="article-layout__share">
            <span className="article-layout__share-label">Share</span>
            <button
              className="article-layout__share-btn"
              onClick={handleCopyLink}
              title="Copy link"
              type="button"
              aria-label="Copy link"
            >
              <FontAwesomeIcon icon={copied ? faCheck : faLink} />
            </button>
            <button
              className="article-layout__share-btn"
              onClick={handleTwitterShare}
              title="Share on X"
              type="button"
              aria-label="Share on X"
            >
              <FontAwesomeIcon icon={faXTwitter} />
            </button>
            <button
              className="article-layout__share-btn"
              onClick={handleLinkedInShare}
              title="Share on LinkedIn"
              type="button"
              aria-label="Share on LinkedIn"
            >
              <FontAwesomeIcon icon={faLinkedinIn} />
            </button>
          </div>
          <button
            className="article-layout__back-bottom"
            onClick={() => navigate('/foundations')}
            type="button"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Foundations</span>
          </button>
        </footer>
      </article>
    </div>
  );
};

export default ArticleLayout;
